import type { Card, CardCount, Color, Shading, Shape } from './types';

export function generateDeck(): Card[] {
  const shapes: Shape[] = ['bolt', 'eye', 'kite'];
  const colors: Color[] = ['red', 'green', 'purple'];
  const shadings: Shading[] = ['solid', 'striped', 'outline'];
  const counts: CardCount[] = [1, 2, 3];

  const deck: Card[] = [];
  let idCounter = 0;

  for (const shape of shapes) {
    for (const color of colors) {
      for (const shading of shadings) {
        for (const count of counts) {
          deck.push({
            id: `card_${idCounter++}_${shape}_${color}_${shading}_${count}`,
            shape,
            color,
            shading,
            count,
          });
        }
      }
    }
  }

  return deck;
}

export function shuffle<T>(array: T[], random: () => number = Math.random): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export function isSet(c1: Card, c2: Card, c3: Card): boolean {
  const isAttrValid = (v1: any, v2: any, v3: any): boolean => {
    return (v1 === v2 && v2 === v3) || (v1 !== v2 && v1 !== v3 && v2 !== v3);
  };

  return (
    isAttrValid(c1.shape, c2.shape, c3.shape) &&
    isAttrValid(c1.color, c2.color, c3.color) &&
    isAttrValid(c1.shading, c2.shading, c3.shading) &&
    isAttrValid(c1.count, c2.count, c3.count)
  );
}

export function findSets(cards: Card[]): [Card, Card, Card][] {
  const validSets: [Card, Card, Card][] = [];
  const n = cards.length;

  for (let i = 0; i < n - 2; i++) {
    for (let j = i + 1; j < n - 1; j++) {
      for (let k = j + 1; k < n; k++) {
        if (isSet(cards[i], cards[j], cards[k])) {
          validSets.push([cards[i], cards[j], cards[k]]);
        }
      }
    }
  }

  return validSets;
}

export function hasValidSet(cards: Card[]): boolean {
  const n = cards.length;
  for (let i = 0; i < n - 2; i++) {
    for (let j = i + 1; j < n - 1; j++) {
      for (let k = j + 1; k < n; k++) {
        if (isSet(cards[i], cards[j], cards[k])) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Returns the first valid SET found among the given cards, or null if none exist.
 */
export function findHint(cards: (Card | null)[]): [Card, Card, Card] | null {
  const activeCards = cards.filter((c): c is Card => c !== null);
  const sets = findSets(activeCards);
  return sets.length > 0 ? sets[0] : null;
}

/**
 * Returns the 0-based indices on the board of the first valid SET, or null if none exist.
 * This provides a code-only way for tests or bots to find a SET directly from a board array.
 */
export function findHintIndices(board: (Card | null)[]): [number, number, number] | null {
  for (let i = 0; i < board.length - 2; i++) {
    const c1 = board[i];
    if (!c1) continue;
    for (let j = i + 1; j < board.length - 1; j++) {
      const c2 = board[j];
      if (!c2) continue;
      for (let k = j + 1; k < board.length; k++) {
        const c3 = board[k];
        if (!c3) continue;
        if (isSet(c1, c2, c3)) {
          return [i, j, k];
        }
      }
    }
  }
  return null;
}

